import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'what-you-get', title: '1. What SSO gives you' },
  { id: 'prerequisites', title: '2. What you need' },
  { id: 'provider', title: '3. Configure your provider' },
  { id: 'connection', title: '4. Create the connection' },
  { id: 'domains', title: '5. Verify your domain' },
  { id: 'enforce', title: '6. Enforcing SSO' },
  { id: 'troubleshooting', title: '7. Troubleshooting' },
];

const EnterpriseSsoPage = () => {
  return (
    <>
      <NextSeo
        title="Enterprise SSO"
        description="Set up OIDC single sign-on with Okta, Microsoft Entra, Google Workspace or any OIDC provider — including DNS domain verification and enforcement."
        canonical="https://chat.co/docs/enterprise-sso"
        openGraph={{
          url: 'https://chat.co/docs/enterprise-sso',
          title: 'Enterprise SSO | Chat.co',
          description:
            'Set up OIDC single sign-on with Okta, Microsoft Entra, Google Workspace or any OIDC provider.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Security',
            tags: ['sso', 'oidc', 'okta', 'entra', 'google workspace', 'security'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Security"
        categoryColor="text-rose-700"
        title="Enterprise SSO"
        description="Put Chat.co behind your identity provider, so joining and leaving your company is the only access decision anyone has to make."
        readingTime="9 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Team, roles & seats',
          href: PageLink.docsTeamRoles,
        }}
        nextArticle={{
          title: 'Security & compliance',
          href: PageLink.docsSecurityCompliance,
        }}
        keyTakeaways={[
          'Chat.co supports OIDC — Okta, Microsoft Entra, Google Workspace and any compliant provider',
          'A domain only routes to SSO after you prove ownership with a DNS TXT record',
          'A domain can be claimed by exactly one connection, so nobody can hijack yours',
          'SSO can provision users as team members or admins — never as a platform administrator',
          'Enforcement removes the password fallback, so test the flow thoroughly before enabling it',
        ]}>
        <p className="lead">
          Once more than a handful of people use Chat.co, the interesting security question stops
          being &ldquo;who has a strong password&rdquo; and becomes &ldquo;what happens the day
          someone leaves&rdquo;. SSO answers it: disable the account in your identity provider and
          Chat.co access goes with it, with no manual step anyone can forget.
        </p>

        <section id="what-you-get" className="scroll-mt-28">
          <h2>1. What SSO gives you</h2>

          <DocsCardGrid columns={2}>
            <DocsCard icon="tabler:door-exit" title="Offboarding that actually works">
              Access is your identity provider&apos;s decision, not a checklist item in a leavers
              process.
            </DocsCard>
            <DocsCard icon="tabler:shield-lock" title="Your policies, everywhere">
              MFA, conditional access, device posture, session length — whatever you enforce at the
              IdP now applies here too.
            </DocsCard>
            <DocsCard icon="tabler:user-plus" title="Provisioning on first login">
              A new hire signs in and gets an account at the role you configured. No invitation to
              chase.
            </DocsCard>
            <DocsCard icon="tabler:list-check" title="One audit trail">
              Sign-in activity lives in the same place as the rest of your access logging.
            </DocsCard>
          </DocsCardGrid>

          <DocsCallout variant="note" title="OIDC only">
            Chat.co implements OpenID Connect. SAML is not currently supported for login — a
            connection can only be created and enabled as OIDC. Every major identity provider speaks
            OIDC, so this is rarely a blocker, but it is worth knowing before you plan the work.
          </DocsCallout>
        </section>

        <section id="prerequisites" className="scroll-mt-28">
          <h2>2. What you need</h2>
          <ul>
            <li>
              <strong>An OIDC identity provider.</strong> Okta, Microsoft Entra ID, Google
              Workspace, or any compliant provider.
            </li>
            <li>
              <strong>Permission to create an application in it.</strong> Usually an IdP
              administrator.
            </li>
            <li>
              <strong>Control of your email domain&apos;s DNS.</strong> You will publish a TXT
              record to prove ownership.
            </li>
            <li>
              <strong>Owner or team-management access in Chat.co.</strong> See{' '}
              <Link href={PageLink.docsTeamRoles}>team, roles &amp; seats</Link>.
            </li>
          </ul>
        </section>

        <section id="provider" className="scroll-mt-28">
          <h2>3. Configure your provider</h2>
          <p>
            Create an OIDC web application in your IdP. The setup guide inside Chat.co gives
            provider-specific paths; broadly:
          </p>

          <DocsTable
            headers={['Provider', 'Where to create the app']}
            rows={[
              [
                <strong key="o">Okta</strong>,
                'Admin → Applications → Create App Integration → OIDC – OpenID Connect → Web Application',
              ],
              [
                <strong key="m">Microsoft Entra</strong>,
                'Entra admin centre → App registrations → New registration',
              ],
              [
                <strong key="g">Google Workspace</strong>,
                'Google Cloud Console → APIs &amp; Services → Credentials → Create credentials → OAuth client ID',
              ],
            ]}
          />

          <p>Whatever the provider, you come away with three values:</p>

          <DocsTable
            headers={['Value', 'What it is']}
            monoFirstColumn
            rows={[
              [
                'issuer',
                'The IdP’s base URL — for Okta, your org URL such as https://acme.okta.com. Chat.co uses it to discover the rest of the endpoints.',
              ],
              ['clientId', 'The application identifier your IdP generated'],
              [
                'clientSecret',
                'The application secret. Stored encrypted, and never returned by any API — if you lose it, generate a new one at the IdP.',
              ],
            ]}
          />

          <DocsCallout variant="warning" title="Set the redirect URI carefully">
            Your IdP application needs Chat.co&apos;s SSO callback registered as an allowed redirect
            URI, exactly — trailing slashes and http-versus-https both matter. The Chat.co SSO setup
            screen shows the exact value to paste. A mismatch here is the single most common reason
            a first SSO login fails.
          </DocsCallout>
        </section>

        <section id="connection" className="scroll-mt-28">
          <h2>4. Create the connection</h2>

          <DocsSteps>
            <DocsStep title="Enter issuer, client ID and secret">
              <p>The three values from the previous section.</p>
            </DocsStep>
            <DocsStep title="List your email domains">
              <p>
                The domains whose users should sign in through this connection —{' '}
                <code>acme.com</code>, and any others your people use.
              </p>
            </DocsStep>
            <DocsStep title="Choose the default role">
              <p>
                What a user provisioned by SSO becomes on first login: <strong>team member</strong>{' '}
                or <strong>admin</strong>. Team member is almost always the right answer — you can
                promote individuals afterwards.
              </p>
            </DocsStep>
            <DocsStep title="Test before you save">
              <p>
                The connection form has a test action that checks your OIDC configuration actually
                reaches the identity provider. Use it on the values you have typed, before
                committing them.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCallout variant="note" title="SSO cannot mint a platform administrator">
            The default role is restricted to team member and admin by design. No SSO configuration
            can provision an account with platform-level privileges, however it is set up.
          </DocsCallout>
        </section>

        <section id="domains" className="scroll-mt-28">
          <h2>5. Verify your domain</h2>
          <p>
            Listing a domain on a connection <em>requests</em> it. Traffic only routes to your SSO
            once you have proven you own it — otherwise anyone could claim{' '}
            <code>yourcompany.com</code> and intercept your sign-ins.
          </p>

          <p>Chat.co gives you a TXT record per domain. Publish it in your DNS:</p>

          <DocsCodeBlock
            language="dns"
            filename="DNS TXT record"
            code={`Host:  _chatco-sso.acme.com
Type:  TXT
Value: chatco-sso-verification=<token shown in the dashboard>`}
          />

          <p>
            Then trigger verification from the connection. Once the record resolves, the domain is
            verified and starts routing.
          </p>

          <DocsCallout variant="tip" title="DNS propagation is the usual delay">
            Most providers publish within minutes; some take up to an hour. If verification fails,
            confirm the record with <code>dig TXT _chatco-sso.acme.com</code> before assuming
            anything is wrong with the connection.
          </DocsCallout>

          <p>
            A domain can be verified by exactly one connection. If verification is refused because
            the domain is already claimed, that is the protection working — get in touch rather than
            working around it.
          </p>
        </section>

        <section id="enforce" className="scroll-mt-28">
          <h2>6. Enforcing SSO</h2>
          <p>A connection has two independent switches, and the difference matters:</p>

          <DocsTable
            headers={['Switch', 'Effect']}
            rows={[
              [
                <strong key="e">Enabled</strong>,
                'SSO works. Users on a verified domain can sign in through your IdP, and the ordinary email sign-in still works alongside it.',
              ],
              [
                <strong key="f">Enforced</strong>,
                'SSO is the only route. Users on that domain can no longer sign in with an emailed code — every login goes through your IdP.',
              ],
            ]}
          />

          <DocsCallout variant="warning" title="Enforce only after real users have signed in">
            Enforcement removes the fallback. If the connection is subtly misconfigured — a wrong
            redirect URI, a scope your IdP will not grant — enforcing turns a fixable problem into a
            lockout for everyone on that domain at once. Enable, have several people sign in
            successfully, then enforce.
          </DocsCallout>

          <p>
            Keep at least one owner account on a domain that is <em>not</em> enforced, or otherwise
            able to reach the account another way. It is the cheapest insurance available against an
            identity provider outage.
          </p>
        </section>

        <section id="troubleshooting" className="scroll-mt-28">
          <h2>7. Troubleshooting</h2>

          <DocsTable
            headers={['Symptom', 'Most likely cause']}
            rows={[
              [
                'The IdP rejects the request immediately',
                'Redirect URI mismatch. Compare what the SSO setup screen shows against what is registered at the IdP, character for character.',
              ],
              [
                'Sign-in succeeds at the IdP but Chat.co refuses',
                'The domain is listed but not verified, or the email address the IdP returned is on a domain the connection does not cover.',
              ],
              [
                'Domain verification keeps failing',
                'The TXT record is not resolving yet, or was created on the wrong host. It must be _chatco-sso.<your-domain>, not the bare domain.',
              ],
              [
                'Users land with the wrong role',
                'The connection’s default role applies at first login only. Change existing people on the Team page; change the default for future ones.',
              ],
              [
                'Nobody on the domain can sign in at all',
                'Enforcement is on and the connection is broken. Contact support — this is why the previous section says to enforce last.',
              ],
            ]}
          />

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:users"
              title="Team, roles &amp; seats"
              href={PageLink.docsTeamRoles}>
              What the roles SSO provisions actually grant.
            </DocsCard>
            <DocsCard
              icon="tabler:shield-check"
              title="Security &amp; compliance"
              href={PageLink.docsSecurityCompliance}>
              The wider data-handling and access-control picture.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default EnterpriseSsoPage;
